import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { ResponseInterceptorOptions as ApiResponseHeaders } from '@quorum/models/xs-misc';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export const API_BASE = new InjectionToken('API_BASE');
export const AUTH_API_BASE = new InjectionToken('AUTH_API_BASE');
export const LAMBDA_API_BASE = new InjectionToken('LAMBDA_API_BASE ');
export const LEGACY_API_BASE = new InjectionToken('LEGACY_API_BASE');
export const LEGACY_AUTH_BASE = new InjectionToken('LEGACY_AUTH_BASE');
export const V4_API_BASE = new InjectionToken<string>('V4_API_BASE');

// Domains routed through v4-staging-api / v4-api. Add a string here to flip
// ALL `v/1/<domain>/**` calls onto the v4 host. Paths are unchanged.
//
// When adding a domain here you must also add a matching Cypress spec at
// `apps/v4-migration-e2e/src/integration/<domain>.spec.ts`. The CI workflow
// (.github/workflows/v4-migration-e2e.yml) runs a pre-flight check
// (`apps/v4-migration-e2e/scripts/check-spec-coverage.js`) that fails the
// build if a V4_DOMAINS entry has no matching spec file, then runs the
// suite. See `apps/v4-migration-e2e/README.md` for the template.
export const V4_DOMAINS: ReadonlySet<string> = new Set<string>(['associates']);

@Injectable()
export class ApiService {
  constructor(
    private http: HttpClient,
    @Inject(API_BASE) public apiBase: string,
    @Inject(AUTH_API_BASE) public authApiBase: string,
    @Inject(LAMBDA_API_BASE) public lambdaApiBase: string,
    @Inject(LEGACY_API_BASE) private legacyApiBase: string,
    @Inject(LEGACY_AUTH_BASE) private legacyAuthBase: string,
    @Optional() @Inject(V4_API_BASE) private v4ApiBase: string | null = null
  ) {}

  private resolveBase(path: string): string {
    const stripped = stripSlash(path);
    const match = stripped.match(/^v\/\d+\/([^/?]+)/);
    if (match && V4_DOMAINS.has(match[1]) && this.v4ApiBase) {
      return this.v4ApiBase;
    }
    return this.apiBase;
  }

  getWithHeaders<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);

    if (opts) {
      return this.http.get(`${this.resolveBase(path)}/${path}`, { ...opts, observe: 'response' }).pipe(
        map((res: any) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.resolveBase(path)}/${path}`, { observe: 'response' }).pipe(
        map((res) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  get<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.resolveBase(path)}/${path}`, opts).pipe(
        map((res) => {
          return res;
        }),
        catchError((err: Response) => {
          return catcher(err, path);
        })
      );
    } else {
      return this.http.get(`${this.resolveBase(path)}/${path}`).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  head<T>(path: string, opts?: any): Observable<T> {
    path = stripSlash(path);
    opts = Object.assign({}, { observe: 'response', responseType: 'json' }, opts);
    return this.http.head(`${this.resolveBase(path)}/${path}`, opts).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err: Response) => catcher(err, path))
    );
  }

  patch<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    return this.http.patch(`${this.resolveBase(path)}/${path}`, body).pipe(
      map((res) => res),
      catchError((err) => catcher(err, path))
    );
  }

  put<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res: any) => {
          return res;
        }),
        catchError((err) => catcher(err, path))
      );
    }
  }

  putWithHeaders<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http
        .put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, {
          ...opts,
          observe: 'response',
        })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});
            return { body: res.body, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    } else {
      return this.http
        .put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, { observe: 'response' })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});

            return { body: res, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    }
  }

  post<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http
        .post(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, opts)
        .pipe(
          map((res: any) => {
            if (opts.observe) return res.body;
            else return res;
          }),
          catchError((err) => catcher(err, path))
        );
    } else {
      return this.http.post(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  delete<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.delete(`${this.resolveBase(path)}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.delete(`${this.resolveBase(path)}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  // Lambda API Implementation START
  lambdaGet<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.lambdaApiBase}/${path}`, opts).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.lambdaApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  lambdaGetWithHeaders<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.lambdaApiBase}/${path}`, { ...opts, observe: 'response' }).pipe(
        map((res: any) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.lambdaApiBase}/${path}`, { observe: 'response' }).pipe(
        map((res) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  lambdaPost<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.post(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.post(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  lambdaPut<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res: any) => {
          return res;
        }),
        catchError((err) => catcher(err, path))
      );
    }
  }

  lambdaPutWithHeaders<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http
        .put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, {
          ...opts,
          observe: 'response',
        })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});
            return { body: res.body, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    } else {
      return this.http
        .put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, { observe: 'response' })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});
            return { body: res, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    }
  }

  lambdaPatch<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    return this.http.patch(`${this.lambdaApiBase}/${path}`, body).pipe(
      map((res) => res),
      catchError((err) => catcher(err, path))
    );
  }

  lambdaDelete<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.delete(`${this.lambdaApiBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.delete(`${this.lambdaApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }
  // Lambda API Implementation END

  // New Cognito Auth API Methods START
  getCognitoAuth<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.authApiBase}/${path}`, opts).pipe(
        map((res) => {
          return res;
        }),
        catchError((err: Response) => {
          return catcher(err, path);
        })
      );
    } else {
      return this.http.get(`${this.authApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  postCognitoAuth<T>(path: string, body: any, opts?: any): Observable<any> {
    if (opts) {
      return this.http.post(`${this.authApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.post(`${this.authApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }
  // New Cognito Auth API Methods END

  // Legacy API Methods START
  legacyPost<T>(path: string, body: any, opts?: any): Observable<any> {
    if (opts) {
      return this.http.post(`${this.legacyApiBase}/${path}`, body, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.post(`${this.legacyApiBase}/${path}`, body).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyPut<T>(path: string, body: any, opts?: any): Observable<any> {
    if (opts) {
      return this.http.put(`${this.legacyApiBase}/${path}`, body, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.put(`${this.legacyApiBase}/${path}`, body).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyGet<T>(path: string, opts?: any): Observable<any> {
    if (opts) {
      return this.http.get(`${this.legacyApiBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.legacyApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyDelete<T>(path: string, opts?: any): Observable<any> {
    if (opts) {
      return this.http.delete(`${this.legacyApiBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.delete(`${this.legacyApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyAuthGet<T>(path: string, opts?: any): Observable<any> {
    if (opts) {
      return this.http.get(`${this.legacyAuthBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.legacyAuthBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }
  // Legacy API Methods END

  // Helper functions START
  getApiUrl() {
    return this.apiBase;
  }
  // Helper functions END
}

function catcher(err: any, path: string): any {
  if (err && (err.name === 'TimeoutError' || err.status === 0)) {
    return throwError({
      reason: `Network connection lost. Please check your WiFi and try again.`,
      statusCode: 0,
    });
  }
  switch (err.status) {
    case 403:
      return throwError({
        reason: `The requested operation is forbidden.`,
        statusCode: 403,
      });
    case 404:
      return throwError({
        reason: `Data for /${path} could not be loaded at this time.`,
        statusCode: 404,
      });
    case 500:
      return throwError({
        reason: `Internal server error. ${err.error.error_description}`,
        statusCode: 500,
      });
    case 503: {
      const body: any = err.error;
      return throwError({
        reason: body.error_description,
        statusCode: 503,
      });
    }
    default:
      return throwError({
        reason: `Unknown error loading /${path}.`,
        statusCode: err.status,
      });
  }
}

/**
 * Fix path if it has a leading slash.
 */
function stripSlash(path: string): string {
  return path.replace(/^\//, '');
}

function emptyStringsToNull(body: any) {
  const bodyCopy = new DeepCopyPipe().transform(body);
  Object.keys(bodyCopy).forEach((entry: any) => {
    if (bodyCopy[entry] === '') {
      bodyCopy[entry] = null;
    }
  });
  return bodyCopy;
}
